import { CheckCircleOutlined, CrownOutlined } from '@ant-design/icons';
import { Button, Card, Spin, Switch, Tag } from 'antd';
import React, { useEffect, useState } from 'react';

import { useAuth } from '../api/AuthContext.js';
import {
    createCheckoutSession,
    getSubscriptionStatus,
    openPaddleCheckout,
} from '../api/subscriptions.js';
import { showToast, updateToast } from '../utils/toastUtils.js';

function Subscription() {
    const auth = useAuth();
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [creatingCheckout, setCreatingCheckout] = useState(false);
    const [isAnnual, setIsAnnual] = useState(true); // Default to annual (better value)

    const loadSubscriptionStatus = async () => {
        try {
            setLoading(true);
            const data = await getSubscriptionStatus();
            setSubscriptionData(data);
        } catch (err) {
            console.error('Error loading subscription:', err);
            showToast('Failed to load subscription status', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSubscriptionStatus();
    }, []);

    const handleSubscribe = async (priceId) => {
        const toastId = showToast('Creating checkout session...', 'loading');

        try {
            setCreatingCheckout(true);

            // Create checkout session with selected price ID
            const response = await createCheckoutSession(priceId);
            const transactionId = response.transactionId;

            if (!transactionId) {
                throw new Error('No transaction ID received');
            }

            updateToast(toastId, 'Opening Paddle checkout...', {
                type: 'success',
                isLoading: false,
                autoClose: 2000,
            });

            // Open Paddle checkout overlay with success callback
            openPaddleCheckout(transactionId, async (data) => {
                // Payment completed successfully
                showToast('Payment successful! Updating subscription...', 'success');

                // Wait a moment for webhook to process
                setTimeout(async () => {
                    await loadSubscriptionStatus();
                    showToast('Subscription activated! 🎉', 'success');
                }, 2000);
            });
        } catch (err) {
            console.error('Error creating checkout:', err);
            updateToast(toastId, 'Failed to create checkout session', {
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
        } finally {
            setCreatingCheckout(false);
        }
    };

    if (loading) {
        return (
            <div className="container main">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    const isActive = subscriptionData?.subscription_status === 'active';
    const isFree = !isActive;

    return (
        <div className="container main">
            <h1>Subscription</h1>

            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                {/* Current Status */}
                <Card style={{ marginBottom: '20px' }}>
                    <h2>Current Plan</h2>
                    <div style={{ marginBottom: '16px' }}>
                        {isActive ? (
                            <Tag color="green" icon={<CrownOutlined />}>
                                PREMIUM - Unlimited Events
                            </Tag>
                        ) : (
                            <Tag color="default">FREE - 1 Event Limit</Tag>
                        )}
                    </div>

                    <div>
                        <p>
                            <strong>Events Created:</strong>{' '}
                            {subscriptionData?.event_count || 0}
                            {isFree && ' / 1'}
                        </p>
                        {isActive && subscriptionData?.subscription_expires_at && (
                            <p>
                                <strong>Renews:</strong>{' '}
                                {new Date(
                                    subscriptionData.subscription_expires_at
                                ).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                </Card>

                {/* Upgrade Section */}
                {isFree && (
                    <Card>
                        <h2>
                            <CrownOutlined /> Upgrade to Premium
                        </h2>

                        {/* Billing Period Toggle */}
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '30px',
                                padding: '20px',
                                background: '#f5f5f5',
                                borderRadius: '8px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    marginBottom: '8px',
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: !isAnnual ? 'bold' : 'normal',
                                        fontSize: '16px',
                                    }}
                                >
                                    Monthly
                                </span>
                                <Switch
                                    checked={isAnnual}
                                    onChange={setIsAnnual}
                                    size="default"
                                />
                                <span
                                    style={{
                                        fontWeight: isAnnual ? 'bold' : 'normal',
                                        fontSize: '16px',
                                    }}
                                >
                                    Annual
                                </span>
                                <Tag color="green" style={{ marginLeft: '8px' }}>
                                    Save 18%
                                </Tag>
                            </div>
                        </div>

                        {/* Pricing Display */}
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '30px',
                                padding: '30px',
                                background: isAnnual
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : '#1890ff',
                                borderRadius: '12px',
                                color: 'white',
                            }}
                        >
                            <div style={{ fontSize: '48px', fontWeight: 'bold' }}>
                                ${isAnnual ? '90' : '9'}
                            </div>
                            <div style={{ fontSize: '18px', marginTop: '8px' }}>
                                per {isAnnual ? 'year' : 'month'}
                            </div>
                            {isAnnual && (
                                <div
                                    style={{
                                        fontSize: '14px',
                                        marginTop: '12px',
                                        opacity: 0.9,
                                    }}
                                >
                                    💰 Save $18/year compared to monthly billing
                                </div>
                            )}
                        </div>

                        {/* Features List */}
                        <div style={{ marginBottom: '30px' }}>
                            <h3>Premium Features:</h3>
                            <ul style={{ fontSize: '16px', lineHeight: '2' }}>
                                <li>
                                    <CheckCircleOutlined
                                        style={{ color: 'green', marginRight: '8px' }}
                                    />
                                    <strong>Unlimited Events</strong> - Create as many as
                                    you need
                                </li>
                                <li>
                                    <CheckCircleOutlined
                                        style={{ color: 'green', marginRight: '8px' }}
                                    />
                                    <strong>Unlimited Participants</strong> - No
                                    restrictions
                                </li>
                                <li>
                                    <CheckCircleOutlined
                                        style={{ color: 'green', marginRight: '8px' }}
                                    />
                                    <strong>Priority Support</strong> - Get help when you
                                    need it
                                </li>
                            </ul>
                        </div>

                        {/* Subscribe Button */}
                        <Button
                            type="primary"
                            size="large"
                            loading={creatingCheckout}
                            onClick={() =>
                                handleSubscribe(
                                    isAnnual
                                        ? process.env.REACT_APP_PADDLE_ANNUAL_PRICE_ID
                                        : process.env.REACT_APP_PADDLE_MONTHLY_PRICE_ID
                                )
                            }
                            style={{
                                width: '100%',
                                height: '50px',
                                fontSize: '18px',
                                fontWeight: 'bold',
                            }}
                        >
                            Subscribe {isAnnual ? 'Annually' : 'Monthly'}
                        </Button>

                        <div
                            style={{
                                textAlign: 'center',
                                marginTop: '16px',
                                fontSize: '13px',
                                color: '#666',
                            }}
                        >
                            Cancel anytime. No questions asked.
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}

export default Subscription;

